import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import DataTable, { Column } from './DataTable';
import { Button } from './ui';
import Modal from './Modal';
import MultiSelect, { SelectId } from './MultiSelect';
import { segmentLabelText } from '../utils/segmentLabel';
import SchedulePromptModal from './SchedulePromptModal';
import type { SchedulePreview, ScheduleSelection, ScheduleAction } from '../types/schedulePrompt';
import '../App.css';
import { translateError } from '../utils/errorMessages';

interface IMemberRef {
  _id: string | number;
  name: string;
}

interface ICourseEnrollment {
  _id: string | number;
  courseId: number;
  member: IMemberRef;
  enrolledAt?: string;
  createdAt?: string;
  // Season-division: the chosen stretch (from the member's active-season
  // membership row). Null when the member has no membership (per-session /
  // not yet bought).
  segmentScheme?: string | null;
  segmentIndex?: number | null;
  periodStart?: string | null;
  periodEnd?: string | null;
}

interface IJoinRequest {
  _id: string | number;
  target: { _id: string | number; name: string };
}

interface IMemberOption {
  _id: string | number;
  name: string;
}

interface CourseEnrollmentsWidgetProps {
  courseId: number;
  /** Bump to force a refetch without changing courseId (e.g. after renewal). */
  refreshKey?: number;
}

const CourseEnrollmentsWidget: React.FC<CourseEnrollmentsWidgetProps> = ({ courseId, refreshKey }) => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();

  const [enrollments, setEnrollments] = useState<ICourseEnrollment[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | number | null>(null);

  // Add members modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [allMembers, setAllMembers] = useState<IMemberOption[]>([]);
  const [addMemberIds, setAddMemberIds] = useState<SelectId[]>([]);
  const [addLoading, setAddLoading] = useState(false);

  // D6 prompt for the pending enroll / un-enroll.
  const [prompt, setPrompt] = useState<{
    action: ScheduleAction;
    memberIds: number[];
    preview: SchedulePreview | null;
    error: string | null;
  } | null>(null);
  const [promptConfirming, setPromptConfirming] = useState(false);

  const canManage = hasPermission('activities:write');

  const fetchEnrollments = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.courses.enrollments.list(courseId);
      setEnrollments(data as ICourseEnrollment[]);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  const fetchPendingCount = useCallback(async () => {
    try {
      const { data } = await api.joinRequests.list({ status: 'pending', type: 'course' });
      const pending = (data as IJoinRequest[]).filter(
        (r) => String(r.target?._id) === String(courseId)
      );
      setPendingCount(pending.length);
    } catch {
      // Non-critical — link still renders if count fails.
    }
  }, [courseId]);

  useEffect(() => {
    fetchEnrollments();
    // refreshKey bumps re-run the fetch even though fetchEnrollments (keyed on
    // courseId) is unchanged after a same-id renewal.
  }, [fetchEnrollments, refreshKey]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount, refreshKey]);

  const openPrompt = async (action: ScheduleAction, memberIds: number[]) => {
    setPrompt({ action, memberIds, preview: null, error: null });
    try {
      const { data } = await api.courses.enrollments.schedulePreview(courseId, action, memberIds);
      setPrompt((p) => (p && p.action === action ? { ...p, preview: data } : p));
    } catch (err: any) {
      const message = translateError(err, t);
      if (action === 'add') {
        // Back to the picker with the selection kept, so the admin can retry.
        setPrompt(null);
        setShowAddModal(true);
        addToast(message, 'error');
        return;
      }
      setPrompt((p) => (p && p.action === action ? { ...p, error: message } : p));
    }
  };

  // Un-enroll is the full season cascade (D9): the prompt only lists it.
  const handleRemove = (memberId: string | number) => {
    if (!canManage) return;
    openPrompt('remove', [Number(memberId)]);
  };

  const handlePromptConfirm = async (selection: ScheduleSelection | null) => {
    if (!prompt) return;
    setPromptConfirming(true);
    if (prompt.action === 'remove') setProcessingId(prompt.memberIds[0]);
    try {
      if (prompt.action === 'add') {
        // Send the ticked subset only when there was something to choose from.
        const templateIds = selection && (prompt.preview?.templates.length ?? 0) > 0
          ? selection.templateIds
          : undefined;
        await api.courses.enrollments.add(courseId, {
          memberIds: prompt.memberIds,
          ...(templateIds !== undefined ? { templateIds } : {}),
        });
        addToast(t('courseEnrollments.addedSuccess'), 'success');
        setAddMemberIds([]);
      } else {
        await api.courses.enrollments.remove(courseId, prompt.memberIds[0]);
        addToast(t('courseEnrollments.removedSuccess'), 'success');
      }
      setPrompt(null);
      fetchEnrollments();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setPromptConfirming(false);
      setProcessingId(null);
    }
  };

  const openAddModal = async () => {
    setAddMemberIds([]);
    setShowAddModal(true);
    if (allMembers.length === 0) {
      try {
        const { data } = await api.members.list();
        setAllMembers((data as IMemberOption[]).map((m) => ({ _id: m._id, name: m.name })));
      } catch {
        // Non-critical — modal still opens with an empty list.
      }
    }
  };

  // The picker hands over to the D6 prompt (D12: choose trainings).
  const handleAddMembers = async () => {
    if (addMemberIds.length === 0) return;
    setAddLoading(true);
    try {
      setShowAddModal(false);
      await openPrompt('add', addMemberIds.map(Number));
    } finally {
      setAddLoading(false);
    }
  };

  const formatDate = (dateString?: string) =>
    dateString ? new Date(dateString).toLocaleString() : '-';

  const formatDateOnly = (dateString?: string | null) =>
    dateString ? new Date(dateString).toLocaleDateString() : '';

  const columns: Column<ICourseEnrollment>[] = useMemo(() => {
    const cols: Column<ICourseEnrollment>[] = [
      {
        header: t('courseEnrollments.member'),
        render: (e) => e.member?.name || t('common.noResults'),
      },
      {
        header: t('courseEnrollments.enrolledAt'),
        render: (e) => formatDate(e.enrolledAt || e.createdAt),
      },
      {
        header: t('courseEnrollments.stretch'),
        render: (e) => {
          const label = segmentLabelText(t, e);
          if (!label) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
          const period =
            e.periodStart && e.periodEnd
              ? `${formatDateOnly(e.periodStart)} – ${formatDateOnly(e.periodEnd)}`
              : '';
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{label}</span>
              {period && (
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{period}</span>
              )}
            </div>
          );
        },
      },
    ];

    if (canManage) {
      cols.push({
        header: t('common.actions'),
        render: (e) => (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleRemove(e.member._id)}
              disabled={processingId === e.member._id}
            >
              {t('courseEnrollments.remove')}
            </Button>
          </div>
        ),
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, canManage, processingId]);

  // Members not yet enrolled — the add endpoint dedupes, but filtering here
  // keeps the picker tidy.
  const enrolledMemberIds = useMemo(
    () => new Set(enrollments.map((e) => String(e.member?._id))),
    [enrollments]
  );
  const memberOptions = useMemo(
    () => allMembers.filter((m) => !enrolledMemberIds.has(String(m._id))),
    [allMembers, enrolledMemberIds]
  );

  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const m of allMembers) names[String(m._id)] = m.name;
    for (const e of enrollments) if (e.member) names[String(e.member._id)] = e.member.name;
    return names;
  }, [allMembers, enrollments]);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <Link
          to="/admin/approvals?tab=course"
          style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textDecoration: 'none' }}
        >
          {t('courses.pendingInApprovals')}
          {pendingCount > 0 && <span className="nav-badge" style={{ marginLeft: '0.4rem' }}>{pendingCount}</span>}
        </Link>

        {canManage && (
          <Button variant="primary" size="sm" onClick={openAddModal}>
            {t('courseEnrollments.addMembers')}
          </Button>
        )}
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <p>{t('courseEnrollments.loading')}</p>
      ) : enrollments.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('courseEnrollments.noEnrollments')}</p>
      ) : (
        <DataTable data={enrollments} columns={columns} />
      )}

      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setAddMemberIds([]); }}
        title={t('courseEnrollments.addMembersTitle')}
        size="medium"
      >
        <div style={{ padding: '0.5rem 0' }}>
          <MultiSelect
            options={memberOptions.map((m) => ({ _id: m._id, name: m.name }))}
            selectedIds={addMemberIds}
            onChange={setAddMemberIds}
            placeholder={t('courseEnrollments.member')}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button
              variant="secondary"
              onClick={() => { setShowAddModal(false); setAddMemberIds([]); }}
              disabled={addLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddMembers}
              loading={addLoading}
              disabled={addMemberIds.length === 0}
            >
              {t('courseEnrollments.addMembers')}
            </Button>
          </div>
        </div>
      </Modal>

      <SchedulePromptModal
        isOpen={!!prompt}
        onClose={() => setPrompt(null)}
        action={prompt?.action ?? 'add'}
        title={t(prompt?.action === 'remove' ? 'schedulePrompt.titleUnenroll' : 'schedulePrompt.titleEnroll')}
        memberLabel={prompt ? t('schedulePrompt.membersLabel', {
          names: prompt.memberIds.map((id) => memberNames[String(id)] ?? `#${id}`).join(', '),
        }) : undefined}
        preview={prompt?.preview ?? null}
        previewLoading={!!prompt && !prompt.preview && !prompt.error}
        previewError={prompt?.error ?? null}
        selectable={prompt?.action === 'add'}
        unenroll={prompt?.action === 'remove'}
        onConfirm={handlePromptConfirm}
        confirming={promptConfirming}
        memberNames={memberNames}
        paymentsLink="/admin/payments/overview"
      />
    </div>
  );
};

export default CourseEnrollmentsWidget;
